import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { SessionStorageService } from '../../../services/session-storage.service';
import { BankAccount } from '../../bank-account/bank-account.model';
import { BankAccountService } from '../../bank-account/bank-account.service';
import { BankReconciliationService } from '../bank-reconciliation.service';
import { BankReconciliationRule } from '../models/bank-reconciliation.model';

@Component({
    selector: 'app-bank-reconciliation-rule-form',
    standalone: true,
    imports: [CommonModule, FormsModule, ButtonModule, CheckboxModule, InputNumberModule, InputTextModule, SelectModule,
        PageShellComponent, PageHeroComponent, PageDataPanelComponent],
    template: `
        <app-page-shell>
            <app-page-hero eyebrow="Auto-Match Rule" [title]="isEdit ? 'Edit Rule' : 'New Rule'" subtitle="Define safe matching criteria and auto-apply thresholds.">
                <div actions>
                    <button pButton type="button" icon="pi pi-list" label="Rules" [outlined]="true" (click)="back()"></button>
                    <button pButton type="button" icon="pi pi-save" label="Save Rule" [loading]="saving" (click)="save()"></button>
                </div>
            </app-page-hero>

            <app-page-data-panel title="Rule Setup" resultText="Configuration">
                <div class="form-grid">
                    <label>
                        <span>Rule name</span>
                        <input pInputText [(ngModel)]="rule.ruleName" placeholder="Exact reference and amount">
                    </label>
                    <label>
                        <span>Bank account scope</span>
                        <p-select [options]="bankAccounts" [(ngModel)]="rule.bankAccountId" optionLabel="accountName" optionValue="id"
                                  placeholder="All bank accounts" [showClear]="true"></p-select>
                    </label>
                    <label>
                        <span>Priority</span>
                        <p-inputNumber [(ngModel)]="rule.priority" [min]="1" [showButtons]="true"></p-inputNumber>
                    </label>
                    <label>
                        <span>Date tolerance days</span>
                        <p-inputNumber [(ngModel)]="rule.dateToleranceDays" [min]="0" [showButtons]="true"></p-inputNumber>
                    </label>
                    <label>
                        <span>Amount tolerance</span>
                        <p-inputNumber [(ngModel)]="rule.amountTolerance" mode="decimal" [min]="0" [minFractionDigits]="2" [maxFractionDigits]="4"></p-inputNumber>
                    </label>
                    <label>
                        <span>Minimum confidence</span>
                        <p-inputNumber [(ngModel)]="rule.minimumConfidenceScore" mode="decimal" [min]="0" [max]="1" [minFractionDigits]="2" [maxFractionDigits]="4"></p-inputNumber>
                    </label>
                    <label>
                        <span>Auto-apply threshold</span>
                        <p-inputNumber [(ngModel)]="rule.autoMatchThreshold" mode="decimal" [min]="0" [max]="1" [minFractionDigits]="2" [maxFractionDigits]="4"></p-inputNumber>
                    </label>
                </div>
            </app-page-data-panel>

            <app-page-data-panel title="Matching Criteria" resultText="Signals">
                <div class="checks">
                    <p-checkbox [(ngModel)]="rule.active" [binary]="true" inputId="active"></p-checkbox><label for="active">Active</label>
                    <p-checkbox [(ngModel)]="rule.matchExactAmount" [binary]="true" inputId="exact"></p-checkbox><label for="exact">Require exact amount</label>
                    <p-checkbox [(ngModel)]="rule.matchReference" [binary]="true" inputId="reference"></p-checkbox><label for="reference">Require reference overlap</label>
                    <p-checkbox [(ngModel)]="rule.matchCounterparty" [binary]="true" inputId="counterparty"></p-checkbox><label for="counterparty">Require counterparty overlap</label>
                    <p-checkbox [(ngModel)]="rule.autoApply" [binary]="true" inputId="autoApply"></p-checkbox><label for="autoApply">Allow safe auto-apply</label>
                </div>
                <div class="form-grid contains-grid">
                    <label>
                        <span>Bank reference contains</span>
                        <input pInputText [(ngModel)]="rule.bankReferenceContains" placeholder="INV, RCPT, transfer id">
                    </label>
                    <label>
                        <span>Description contains</span>
                        <input pInputText [(ngModel)]="rule.descriptionContains" placeholder="narration keyword">
                    </label>
                    <label>
                        <span>Counterparty contains</span>
                        <input pInputText [(ngModel)]="rule.counterpartyContains" placeholder="customer/vendor keyword">
                    </label>
                    <label>
                        <span>Book reference contains</span>
                        <input pInputText [(ngModel)]="rule.bookReferenceContains" placeholder="journal/receipt/voucher keyword">
                    </label>
                </div>
            </app-page-data-panel>
        </app-page-shell>
    `,
    styles: [`
        .form-grid { display: grid; grid-template-columns: repeat(3, minmax(180px, 1fr)); gap: 1rem; }
        label { display: grid; gap: .35rem; color: #334155; }
        label span { color: #64748b; font-size: .82rem; }
        input, p-select, p-inputNumber { width: 100%; }
        .checks { display: flex; flex-wrap: wrap; align-items: center; gap: .65rem 1rem; margin-bottom: 1rem; }
        .checks label { display: inline; }
        .contains-grid { grid-template-columns: repeat(2, minmax(220px, 1fr)); }
        @media (max-width: 900px) { .form-grid, .contains-grid { grid-template-columns: 1fr; } }
    `]
})
export class BankReconciliationRuleFormComponent implements OnInit {
    rule: BankReconciliationRule = this.emptyRule();
    bankAccounts: BankAccount[] = [];
    saving = false;
    isEdit = false;
    private ruleId?: number;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private bankReconciliationService: BankReconciliationService,
        private bankAccountService: BankAccountService,
        private sessionStorageService: SessionStorageService
    ) {}

    ngOnInit(): void {
        this.ruleId = Number(this.route.snapshot.paramMap.get('ruleId')) || undefined;
        this.isEdit = !!this.ruleId;
        this.loadBankAccounts();
        if (this.ruleId) {
            this.bankReconciliationService.getRules(this.companyId()).subscribe(rules => {
                this.rule = rules.find(rule => rule.ruleId === this.ruleId) ?? this.emptyRule();
            });
        }
    }

    loadBankAccounts(): void {
        this.bankAccountService.getBankAccounts('Company', this.companyId(), '', 0, 100).subscribe(result => this.bankAccounts = result.items ?? []);
    }

    save(): void {
        if (!this.rule.ruleName?.trim()) return;
        this.saving = true;
        const request = { ...this.rule, owningCompanyId: this.companyId() };
        const save$ = this.ruleId
            ? this.bankReconciliationService.updateRule(this.ruleId, request)
            : this.bankReconciliationService.createRule(request);
        save$.subscribe({
            next: () => {
                this.saving = false;
                this.back();
            },
            error: () => this.saving = false
        });
    }

    back(): void {
        this.router.navigate(['/accounts/enterprise/bank-reconciliation/rules']);
    }

    private emptyRule(): BankReconciliationRule {
        return {
            owningCompanyId: this.companyId(),
            ruleName: '',
            priority: 100,
            active: true,
            matchExactAmount: true,
            matchReference: true,
            matchCounterparty: false,
            dateToleranceDays: 3,
            amountTolerance: 0,
            minimumConfidenceScore: 0.8,
            autoMatchThreshold: 0.95,
            autoApply: false
        };
    }

    private companyId(): number {
        return this.sessionStorageService.getCompany()?.id ?? 0;
    }
}
